import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Badge,
    Button,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    CardFooter,
    Pagination,
    PaginationItem,
    PaginationLink
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import { getAuditLogs, getAuditLogFilters } from "api/ApiServices";
import Avatar from "../../components/Common/Avatar.js";



/* ═══════════════════════════════════════════════════
   INLINE STYLES – Premium Glassmorphism & Modern UI
   ═══════════════════════════════════════════════════ */
const styles = {
    glassCard: {
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "16px",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        boxShadow: "0 8px 32px rgba(31, 38, 135, 0.07)",
        overflow: "visible"
    },
    filterBar: {
        background: "linear-gradient(135deg, #f8f9fe 0%, #eef1f8 100%)",
        borderRadius: "12px",
        padding: "20px 24px",
        margin: "0 0 24px 0",
        border: "1px solid rgba(0,0,0,0.04)",
        display: "flex",
        gap: "10px",
        overflowX: "auto",
        alignItems: "center"
    },
    tabPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        background: "#fff",
        borderRadius: "10px",
        padding: "10px 18px",
        border: "1px solid #e2e8f0",
        fontSize: "14px",
        fontWeight: 600,
        color: "#525f7f",
        cursor: "pointer",
        transition: "all 0.2s ease",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        whiteSpace: "nowrap"
    },
    tabPillActive: {
        background: "linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)",
        color: "#fff",
        border: "1px solid transparent",
        boxShadow: "0 4px 12px rgba(94,114,228,0.3)",
    },
    searchContainer: {
        background: "#fff",
        borderRadius: "10px",
        border: "1px solid #e2e8f0",
        padding: "2px 12px",
        width: "100%",
        maxWidth: "400px",
        display: "flex",
        alignItems: "center",
    },
    logEntry: {
        fontSize: "13px",
        transition: "background 0.2s ease",
        cursor: "pointer"
    },
    avatar: {
        width: "32px",
        height: "32px",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: 700,
        color: "#fff",
        marginRight: "10px"
    },
    detailPanel: {
        background: "#f8f9fe",
        borderRadius: "12px",
        padding: "20px",
        border: "1px solid #e9ecef"
    },
    timestamp: {
        fontFamily: "'Roboto Mono', monospace",
        fontSize: "12px",
        color: "#adb5bd"
    }
};

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
const Logs = () => {
    const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
        const totalPagesCount = Math.ceil(totalItems / limit);
        if (totalPagesCount <= 1) return null;

        const pages = [];
        const delta = 2;

        pages.push(
            <PaginationItem key={1} active={currentPage === 1}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        if (currentPage > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        const rangeStart = Math.max(2, currentPage - delta);
        const rangeEnd = Math.min(totalPagesCount - 1, currentPage + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={currentPage === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (currentPage < totalPagesCount - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        if (totalPagesCount > 1) {
            pages.push(
                <PaginationItem key={totalPagesCount} active={currentPage === totalPagesCount}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPagesCount); }}>{totalPagesCount}</PaginationLink>
                </PaginationItem>
            );
        }

        return pages;
    };

    const [activeTab, setActiveTab] = useState("Admin");
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedLog, setSelectedLog] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalLogs, setTotalLogs] = useState(0);
    const [logsPerPage] = useState(10);
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Filters state
    const [filterOptions, setFilterOptions] = useState({ admins: [], actions: [] });
    const [adminFilter, setAdminFilter] = useState("All Admins");
    const [actionFilter, setActionFilter] = useState("All Actions");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");


    // Map frontend tab to backend log type
    const tabToLogType = {
        "Admin": "Admin Actions",
        "Moderation": "Moderation Decisions",
        "Wallet": "Wallet Overrides",
        "KYC": "KYC Decisions"
    };

    const fetchLogs = async (type, page = 1) => {
        setLoading(true);
        try {
            const backendType = tabToLogType[type] || "Admin Actions";
            const response = await getAuditLogs({
                type: backendType,
                admin_name: adminFilter,
                action: actionFilter,
                start_date: startDate,
                end_date: endDate,
                search: debouncedSearch,
                page: page,
                limit: logsPerPage
            });

            if (response.status && response.payload && response.payload.logs) {
                setLogs(response.payload.logs);
                setTotalLogs(response.payload.total || 0);
            } else {
                setLogs([]);
                setTotalLogs(0);
            }
        } catch (error) {
            console.error("Error fetching logs:", error);
            setLogs([]);
            setTotalLogs(0);
        } finally {
            setLoading(false);
        }
    };

    const fetchFilterOptions = async () => {
        try {
            const response = await getAuditLogFilters();
            if (response.status && response.payload) {
                setFilterOptions({
                    admins: response.payload.admins || [],
                    actions: response.payload.actions || []
                });
            }
        } catch (error) {
            console.error("Error fetching filter options:", error);
        }
    };

    useEffect(() => {
        fetchFilterOptions();
    }, []);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1); // Reset to page 1 on search
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // Reset page to 1 on filter/tab change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, adminFilter, actionFilter, startDate, endDate]);


    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .table-responsive { overflow-x: visible !important; overflow-y: visible !important; }
            .dropdown-menu { z-index: 1060 !important; }
            .pagination .page-link {
                border-radius: 8px !important;
                margin: 0 3px;
                border: 1px solid #e2e8f0;
                color: #525f7f;
                font-weight: 600;
                font-size: 13px;
                padding: 8px 14px;
                transition: all 0.2s ease;
            }
            .pagination .page-item.active .page-link {
                background: linear-gradient(135deg, #5e72e4 0%, #825ee4 100%);
                border-color: transparent;
                color: #fff;
                box-shadow: 0 4px 12px rgba(94,114,228,0.2);
            }
            .pagination .page-item.disabled .page-link {
                background: #f8f9fe;
                color: #adb5bd;
            }
        `;
        document.head.appendChild(style);

        fetchLogs(activeTab, currentPage);

        return () => document.head.removeChild(style);
    }, [activeTab, adminFilter, actionFilter, startDate, endDate, debouncedSearch, currentPage]);


    const toggleModal = () => setModalOpen(!modalOpen);

    const openLogDetails = (log) => {
        setSelectedLog(log);
        toggleModal();
    };

    const getStatusBadge = (log) => {
        // Since we don't have a status field in the new model, we can derive it or use a default
        // For now, most admin actions are considered "Success" if logged.
        return <Badge color="success">Success</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid style={{ position: "relative", zIndex: 1 }}>

                {/* ── SECURITY Cockpit Tabs & Filters ───────────────────── */}
                <div style={styles.filterBar}>
                    {["Admin", "Moderation", "Wallet", "KYC"].map((tab) => (
                        <div
                            key={tab}
                            style={{
                                ...styles.tabPill,
                                ...(activeTab === tab ? styles.tabPillActive : {})
                            }}
                            onClick={() => setActiveTab(tab)}
                        >
                            <i className={`fas ${tab === 'Admin' ? 'fa-user-shield' : tab === 'Moderation' ? 'fa-gavel' : tab === 'Wallet' ? 'fa-wallet' : 'fa-id-card'}`} />
                            {tab + " Decisions"}
                        </div>
                    ))}

                    <div className="ml-auto" style={styles.searchContainer}>
                        <i className="fas fa-search text-muted mr-3" />
                        <Input
                            placeholder="Search logs..."
                            className="border-0 p-0 shadow-none bg-transparent"
                            style={{ height: "40px", fontSize: "14px" }}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* ── ADVANCED FILTERS ─────────────────────────────────── */}
                <div style={{ ...styles.filterBar, marginTop: "-12px", background: "#fff", border: "1px solid #e2e8f0", padding: "24px 30px" }}>
                    <Row className="w-100 align-items-end">
                        <Col lg="3" md="6" className="mb-3 mb-lg-0">
                            <label className="text-xs text-uppercase text-muted font-weight-bold mb-2 d-block" style={{ letterSpacing: "0.5px" }}>Filter Admin</label>
                            <Input
                                type="select"
                                value={adminFilter}
                                onChange={(e) => setAdminFilter(e.target.value)}
                                style={{ borderRadius: "8px", fontSize: "14px", height: "45px", border: "1px solid #e2e8f0" }}
                            >
                                <option>All Admins</option>
                                {filterOptions.admins.map(admin => <option key={admin}>{admin}</option>)}
                            </Input>
                        </Col>

                        <Col lg="3" md="6" className="mb-3 mb-lg-0">
                            <label className="text-xs text-uppercase text-muted font-weight-bold mb-2 d-block" style={{ letterSpacing: "0.5px" }}>Action Performed</label>
                            <Input
                                type="select"
                                value={actionFilter}
                                onChange={(e) => setActionFilter(e.target.value)}
                                style={{ borderRadius: "8px", fontSize: "14px", height: "45px", border: "1px solid #e2e8f0" }}
                            >
                                <option>All Actions</option>
                                {filterOptions.actions.map(action => <option key={action}>{action}</option>)}
                            </Input>
                        </Col>

                        <Col lg="4" md="12" className="mb-3 mb-lg-0">
                            <label className="text-xs text-uppercase text-muted font-weight-bold mb-2 d-block" style={{ letterSpacing: "0.5px" }}>Date Range</label>
                            <div className="d-flex align-items-center">
                                <Input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    style={{ borderRadius: "8px", fontSize: "14px", height: "45px", border: "1px solid #e2e8f0" }}
                                />
                                <span className="mx-3 text-muted font-weight-600">to</span>
                                <Input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    style={{ borderRadius: "8px", fontSize: "14px", height: "45px", border: "1px solid #e2e8f0" }}
                                />
                            </div>
                        </Col>

                        <Col lg="2" md="12" className="text-right">
                            {/* <Button
                                color="secondary"
                                outline
                                style={{ borderRadius: "8px", height: "45px", padding: "0 20px" }}
                                onClick={() => {
                                    setAdminFilter("All Admins");
                                    setActionFilter("All Actions");
                                    setStartDate("");
                                    setEndDate("");
                                }}
                            >
                                <i className="fas fa-redo mr-2" /> Reset
                            </Button> */}
                        </Col>
                    </Row>
                </div>



                {/* ── LOGS TABLE ─────────────────────────────────────── */}
                <Row>
                    <Col xs="12">
                        <Card className="shadow" style={styles.glassCard}>

                            <CardHeader className="border-0 bg-transparent py-4 d-flex flex-wrap justify-content-between align-items-center">
                                <div>
                                    <h3 className="mb-0 text-dark font-weight-700">
                                        <i className="ni ni-archive-2 text-gray mr-2" /> {activeTab} Stream
                                    </h3>
                                    <span className="text-xs text-muted">
                                        A full chronological audit trail of system interventions.
                                    </span>
                                </div>

                                <Button
                                    color="default"
                                    outline
                                    size="sm"
                                    onClick={() => fetchLogs(activeTab)}
                                    className="mt-2 mt-md-0"
                                >
                                    <i className="fas fa-sync mr-2" /> Refresh
                                </Button>
                            </CardHeader>

                            {/*  MOBILE SCROLL FIX */}
                            <div
                                style={{
                                    width: "100%",
                                    overflowX: "auto",
                                    WebkitOverflowScrolling: "touch"
                                }}
                            >
                                <Table
                                    className="align-items-center table-flush"
                                    hover
                                    style={{
                                        minWidth: "1100px", //  Important
                                    }}
                                >
                                    <thead className="thead-light">
                                        <tr>
                                            <th style={{ whiteSpace: "nowrap", padding: "16px 24px" }}>
                                                Admin / System
                                            </th>
                                            <th style={{ whiteSpace: "nowrap" }}>
                                                Action Details
                                            </th>
                                            <th style={{ whiteSpace: "nowrap" }}>
                                                Target Object
                                            </th>
                                            <th style={{ whiteSpace: "nowrap" }}>
                                                IP Address
                                            </th>
                                            <th style={{ whiteSpace: "nowrap" }}>
                                                Timestamp
                                            </th>
                                            <th
                                                className="text-right"
                                                style={{ whiteSpace: "nowrap", paddingRight: "24px" }}
                                            >
                                                Action
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5">
                                                    <i className="fas fa-spinner fa-spin fa-2x text-primary" />
                                                </td>
                                            </tr>
                                        ) : logs.length > 0 ? (
                                            logs.map((log) => (
                                                <tr key={log.al_uuid} onClick={() => openLogDetails(log)}>

                                                    <td style={{ whiteSpace: "nowrap", padding: "16px 24px" }}>
                                                        <div className="d-flex align-items-center">
                                                            <Avatar
                                                                name={log.al_admin_name}
                                                                size="sm"
                                                                className="mr-2"
                                                            />
                                                            <span className="font-weight-bold text-dark">
                                                                {log.al_admin_name}
                                                            </span>
                                                        </div>
                                                    </td>

                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                        <span className="text-dark font-weight-600">
                                                            {log.al_action}
                                                        </span>
                                                        <br />
                                                        <span className="text-xs text-muted">
                                                            Category: {log.al_type}
                                                        </span>
                                                    </td>

                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                        {log.target ? (
                                                            <div className="d-flex align-items-center">
                                                                <Avatar
                                                                    src={log.target.image}
                                                                    name={log.target.name}
                                                                    size="sm"
                                                                    className="mr-2"
                                                                />
                                                                <div>
                                                                    <span className="text-dark font-weight-600 d-block">
                                                                        {log.target.name}
                                                                    </span>
                                                                    <span className="text-xs text-dark">
                                                                        {log.target.email}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <Badge color="secondary">
                                                                System Reference
                                                            </Badge>
                                                        )}
                                                    </td>

                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                        {log.al_ip || "N/A"}
                                                    </td>

                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                        {formatDate(log.al_created_at)}
                                                    </td>

                                                    <td className="text-right" style={{ whiteSpace: "nowrap", paddingRight: "24px" }}>
                                                        <Button
                                                            color="info"
                                                            outline
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openLogDetails(log);
                                                            }}
                                                        >
                                                            Inspect
                                                        </Button>
                                                    </td>

                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5">
                                                    No logs found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* FOOTER SAME */}
                            <CardFooter className="py-4 border-top bg-transparent d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    Showing {(currentPage - 1) * logsPerPage + 1} to{" "}
                                    {Math.min(currentPage * logsPerPage, totalLogs)} of{" "}
                                    {totalLogs} system events
                                </small>

                                {totalLogs > logsPerPage && (
                                    <nav>
                                        <Pagination className="pagination justify-content-end mb-0">
                                            <PaginationItem disabled={currentPage === 1}>
                                                <PaginationLink
                                                    href="#pablo"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setCurrentPage(currentPage - 1);
                                                    }}
                                                >
                                                    <i className="fas fa-angle-left" />
                                                </PaginationLink>
                                            </PaginationItem>

                                            {renderPaginationItems(
                                                currentPage,
                                                totalLogs,
                                                logsPerPage,
                                                setCurrentPage
                                            )}

                                            <PaginationItem
                                                disabled={
                                                    currentPage ===
                                                    Math.ceil(totalLogs / logsPerPage) ||
                                                    totalLogs === 0
                                                }
                                            >
                                                <PaginationLink
                                                    href="#pablo"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setCurrentPage(currentPage + 1);
                                                    }}
                                                >
                                                    <i className="fas fa-angle-right" />
                                                </PaginationLink>
                                            </PaginationItem>
                                        </Pagination>
                                    </nav>
                                )}
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* ── LOG INSPECTION MODAL ─────────────────────────── */}
            <Modal isOpen={modalOpen} toggle={toggleModal} centered size="lg">
                <ModalHeader toggle={toggleModal} className="border-0">
                    <span className="text-uppercase ls-1 text-muted font-weight-bold">Security Inspection: LOG #{selectedLog?.al_uuid.substring(0, 8)}</span>
                </ModalHeader>
                <ModalBody className="pt-0">
                    {selectedLog && (
                        <div className="px-3 pb-4">
                            <Row className="mb-4">
                                <Col md="6">
                                    <h6 className="text-xs text-uppercase text-muted mb-2 ls-1">Intervention Type</h6>
                                    <h2 className="mb-0 text-dark">{selectedLog.al_action}</h2>
                                    <div className="mt-2">{getStatusBadge(selectedLog)}</div>
                                </Col>
                                <Col md="6" className="text-right">
                                    <h6 className="text-xs text-uppercase text-muted mb-2 ls-1">Occurred At</h6>
                                    <span className="font-weight-bold" style={styles.timestamp}>{formatDate(selectedLog.al_created_at)}</span>
                                </Col>
                            </Row>

                            <hr />

                            <Row className="mb-4">
                                <Col md="6">
                                    <div style={styles.detailPanel}>
                                        <h6 className="text-xs text-uppercase text-muted mb-3 ls-1">Intervention Actor</h6>
                                        <div className="d-flex align-items-center">
                                            <Avatar
                                                name={selectedLog.al_admin_name}
                                                size="sm"
                                                className="mr-3"
                                            />
                                            <div>
                                                <span className="font-weight-bold text-dark d-block" style={{ fontSize: "16px" }}>{selectedLog.al_admin_name}</span>
                                                <span className="text-xs text-muted">System Administrator</span>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md="6">
                                    <div style={styles.detailPanel}>
                                        <h6 className="text-xs text-uppercase text-muted mb-3 ls-1">Affected Object</h6>
                                        {selectedLog.target ? (
                                            <div className="d-flex align-items-center">
                                                <Avatar
                                                    src={selectedLog.target.image}
                                                    name={selectedLog.target.name}
                                                    size="sm"
                                                    className="mr-3"
                                                />
                                                <div>
                                                    <span className="font-weight-bold text-dark d-block" style={{ fontSize: "16px" }}>{selectedLog.target.name}</span>
                                                    <span className="text-xs text-muted">{selectedLog.target.email || "System Reference"}</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="p-2 bg-light rounded text-dark text-xs italic">
                                                The specific object reference is no longer available in the active directory.
                                            </div>
                                        )}
                                        <div className="text-xs text-muted mt-2">Type: {selectedLog.al_target_type || "N/A"}</div>
                                    </div>
                                </Col>

                            </Row>

                            {selectedLog.al_details && (
                                <div className="mb-4 pt-3">
                                    <h6 className="text-xs text-uppercase text-muted mb-2 ls-1">Action Details</h6>
                                    <div className="p-3 bg-white border rounded">
                                        {Object.entries(selectedLog.al_details).map(([key, value]) => (
                                            <div key={key} className="d-flex align-items-start mb-2">
                                                <span className="font-weight-bold text-muted text-uppercase text-xs mr-3" style={{ minWidth: "120px", marginTop: "2px" }}>
                                                    {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}
                                                </span>
                                                <span className="text-dark" style={{ wordBreak: "break-word" }}>
                                                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <Row className="mb-4">
                                <Col>
                                    <h6 className="text-xs text-uppercase text-muted mb-2 ls-1">Origin IP</h6>
                                    <Badge color="secondary">{selectedLog.al_ip || "Unknown"}</Badge>
                                </Col>
                            </Row>

                            <Row>
                                <Col>
                                    <Button color="secondary" block onClick={toggleModal}>Close Inspection</Button>
                                </Col>
                                {/* <Col>
                                    <Button color="primary" block onClick={() => Swal.fire("Export Sent", "Log trace has been exported.", "success")}>
                                        <i className="fas fa-file-export mr-2" /> Export Trace
                                    </Button>
                                </Col> */}
                            </Row>
                        </div>
                    )}
                </ModalBody>
            </Modal>
        </>
    );
};

export default Logs;
